import { createSlice } from '@reduxjs/toolkit';
import { RootState } from '..';
import {
  adminCreateNewStructuredQuestion,
  approveStructuredEssayQuestion,
  deleteStructuredEssayQuestion,
  generateStructuredEssayQuestion,
  getStructuredEssayQuestionById,
  getStructuredEssayQuestionList,
  loadMoreStructuredEssayQuestionList,
  updateStructuredEssayQuestion,
} from './StructuredEssayAdminApi';
import { ISubTopic } from '../SubTopicSlice';
import { IBaseUser } from '../UserSlice';
import {
  getAiResponse,
  getRandomStructuredQuestionsBySubTopic,
} from './StructuredEssayApi';

export type TQuestionDifficulty = 'EASY' | 'MEDIUM' | 'HARD' | 'ANY';

export interface ISubQuestion {
  _id?: string;
  question: string;
  marks: number;
  markingScheme: string;
  sortingOrder: number;
}

export interface IStructuredEssayQuestion {
  _id?: string;
  details: string;
  difficulty: TQuestionDifficulty;
  marks: number;
  subQuestions: Array<ISubQuestion>;
  source?: string;
  tags?: string[];
  subTopics?: Array<{ subTopic: ISubTopic; paper: string }>;
  isApproved?: boolean;
  approvedBy?: IBaseUser;
  aiEvaluations?: {
    [questionId: string]: {
      [subQuestionId: string]: {
        verdict: 'Correct' | 'Partially Correct' | 'Incorrect' | string;
        givenMarks: number;
        analysis: string;
      };
    };
  };
}

export interface IstructuredEssayQuestionState {
  status: 'idle' | 'loading' | 'rejected';
  questions: Array<IStructuredEssayQuestion>;
  count: number;
  generatedQuestions: Array<IStructuredEssayQuestion>;
  threadId?: string;
  question?: IStructuredEssayQuestion;
  aiEvaluations?: {
    [questionId: string]: {
      [subQuestionId: string]: {
        verdict: 'Correct' | 'Partially Correct' | 'Incorrect' | string;
        givenMarks: number;
        analysis: string;
      };
    };
  };
}

const initialState: IstructuredEssayQuestionState = {
  status: 'idle',
  questions: [],
  count: 0,
  generatedQuestions: [],
  threadId: undefined,
  question: undefined,
  aiEvaluations: {},
};

export const structuredEssayQuestionSlice = createSlice({
  name: 'structuredEssayQuestion',
  initialState,
  reducers: {
    reset: (state) => {
      state.status = 'idle';
      state.generatedQuestions = [];
      state.threadId = undefined;
    },
    clearGeneratedQuestions: (state) => {
      state.status = 'idle';
      state.generatedQuestions = [];
    },
    resetQuestionList: (state) => {
      state.questions = [];
      state.count = 0;
    },
    resetAIEvaluations: (state) => {
      state.aiEvaluations = {};
    },
    addQuestion: (state, action) => {
      if (!state.generatedQuestions) {
        state.generatedQuestions = [];
      }
      state.generatedQuestions.push(action.payload);
    },
    setThreadId: (state, action) => {
      state.threadId = action.payload;
    },
    setStreamingStatus: (state, action) => {
      state.status = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(generateStructuredEssayQuestion.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(generateStructuredEssayQuestion.fulfilled, (state, action) => {
        state.status = 'idle';
        state.generatedQuestions = action.payload.questions || [];
        if (action.payload.threadId) {
          state.threadId = action.payload.threadId;
        }
      })
      .addCase(generateStructuredEssayQuestion.rejected, (state) => {
        state.status = 'rejected';
        state.threadId = undefined;
      })
      // ADMIN CREATE NEW QUESTION
      .addCase(adminCreateNewStructuredQuestion.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(adminCreateNewStructuredQuestion.fulfilled, (state, action) => {
        state.status = 'idle';
        state.questions = [...state.questions, ...action.payload];
        state.count += action.payload.length;
        state.generatedQuestions = [];
      })
      .addCase(adminCreateNewStructuredQuestion.rejected, (state) => {
        state.status = 'rejected';
      })
      // GET QUESTION LIST
      .addCase(getStructuredEssayQuestionList.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getStructuredEssayQuestionList.fulfilled, (state, action) => {
        state.status = 'idle';
        state.questions = action.payload.docs || [];
        state.count = action.payload.totalDocs;
      })
      .addCase(getStructuredEssayQuestionList.rejected, (state) => {
        state.status = 'rejected';
      })
      // LOAD MORE QUESTION LIST
      .addCase(loadMoreStructuredEssayQuestionList.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(
        loadMoreStructuredEssayQuestionList.fulfilled,
        (state, action) => {
          state.status = 'idle';
          state.questions = [
            ...state.questions,
            ...(action.payload.docs || []),
          ];
          state.count = action.payload.totalDocs;
        }
      )
      .addCase(loadMoreStructuredEssayQuestionList.rejected, (state) => {
        state.status = 'rejected';
      })
      // GET QUESTIONS BY ID
      .addCase(getStructuredEssayQuestionById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getStructuredEssayQuestionById.fulfilled, (state, action) => {
        state.status = 'idle';
        state.question = action.payload;
      })
      .addCase(getStructuredEssayQuestionById.rejected, (state) => {
        state.status = 'rejected';
      })
      // APPROVE QUESTION
      .addCase(approveStructuredEssayQuestion.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(approveStructuredEssayQuestion.fulfilled, (state, action) => {
        state.status = 'idle';
        state.questions = state.questions.map((item) => {
          if (item._id === action.payload._id) {
            return {
              ...item,
              isApproved: action.payload.isApproved,
              approvedBy: action.payload.approvedBy,
            };
          }
          return item;
        });

        if (state.question && state.question._id === action.payload._id) {
          state.question = {
            ...state.question,
            isApproved: action.payload.isApproved,
            approvedBy: action.payload.approvedBy,
          };
        }
      })
      .addCase(approveStructuredEssayQuestion.rejected, (state) => {
        state.status = 'rejected';
      })
      // DELETE QUESTION
      .addCase(deleteStructuredEssayQuestion.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteStructuredEssayQuestion.fulfilled, (state, action) => {
        state.status = 'idle';
        const newList = state.questions.filter(
          (question) => question._id !== action.payload._id
        );
        if (newList.length !== state.questions.length) {
          state.count = Math.max(0, state.count - 1);
        }
        state.questions = newList;
      })
      .addCase(deleteStructuredEssayQuestion.rejected, (state) => {
        state.status = 'rejected';
      })
      // UPDATE QUESTION
      .addCase(updateStructuredEssayQuestion.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateStructuredEssayQuestion.fulfilled, (state, action) => {
        state.status = 'idle';
        const newList = state.questions;

        const index = newList.findIndex(
          (item) => item._id === action.payload._id
        );

        if (index > -1) {
          newList[index] = action.payload;
        }

        state.questions = newList;

        if (
          state.question &&
          action.payload &&
          state.question._id === action.payload._id
        ) {
          state.question = {
            ...state.question,
            ...action.payload,
          };
        }
      })
      .addCase(updateStructuredEssayQuestion.rejected, (state) => {
        state.status = 'rejected';
      })
      // Get Random Structured Essay Questions by SubTopic
      .addCase(getRandomStructuredQuestionsBySubTopic.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(
        getRandomStructuredQuestionsBySubTopic.fulfilled,
        (state, action) => {
          state.status = 'idle';
          state.questions = action.payload;
        }
      )
      .addCase(getRandomStructuredQuestionsBySubTopic.rejected, (state) => {
        state.status = 'rejected';
      })
      // Get AI Response
      .addCase(getAiResponse.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getAiResponse.fulfilled, (state, action) => {
        state.status = 'idle';
        const { questionId, evaluations } = action.payload;

        state.aiEvaluations = {
          ...state.aiEvaluations,
          [questionId]: evaluations.reduce((acc, item) => {
            acc[item.subQuestionId] = {
              verdict: item.verdict,
              givenMarks: item.givenMarks,
              analysis: item.analysis,
            };
            return acc;
          }, {} as Record<string, { verdict: string; givenMarks: number; analysis: string }>),
        };
      })
      .addCase(getAiResponse.rejected, (state) => {
        state.status = 'rejected';
      });
  },
});

export const getStructuredEssayQuestionState = (
  state: RootState
): RootState['structuredEssayQuestion'] => state.structuredEssayQuestion;

export const {
  reset,
  clearGeneratedQuestions,
  resetQuestionList,
  resetAIEvaluations,
  addQuestion,
  setThreadId,
  setStreamingStatus,
} = structuredEssayQuestionSlice.actions;

export * from './StructuredEssayAdminApi';
export default structuredEssayQuestionSlice.reducer;
